import { useCallback } from 'react';
import { isRuleEmpty, type FilterRule, type TableFiltersContextValue } from '@/lib/tableFilters';
import {
  usePersistedColumnFilters,
  type PersistedColumnFiltersOptions,
  type UsePersistedColumnFiltersReturn,
} from '@/hooks/usePersistedColumnFilters';

export interface UseEntityTableFiltersOptions<T> extends PersistedColumnFiltersOptions {
  matchesRule: (item: T, columnId: string, rule: FilterRule) => boolean;
}

export interface UseEntityTableFiltersReturn<T>
  extends UsePersistedColumnFiltersReturn,
    TableFiltersContextValue<T> {}

export function useEntityTableFilters<T>(
  options: UseEntityTableFiltersOptions<T>
): UseEntityTableFiltersReturn<T> {
  const { matchesRule, ...persistedOptions } = options;
  const persisted = usePersistedColumnFilters(persistedOptions);
  const { filtersEnabled, filters } = persisted;

  const filterItems = useCallback(
    (items: T[]): T[] => {
      if (!filtersEnabled) return items;
      const activeEntries = Object.entries(filters).filter(([, rule]) => !isRuleEmpty(rule));
      if (activeEntries.length === 0) return items;
      return items.filter((item) =>
        activeEntries.every(([columnId, rule]) => matchesRule(item, columnId, rule))
      );
    },
    [filtersEnabled, filters, matchesRule]
  );

  return {
    ...persisted,
    filterItems,
  };
}
